import { forwardRef } from 'react';
import { Link as RouterLink } from 'react-router';

type Props = Omit<React.ComponentPropsWithoutRef<'a'>, 'href'> & {
  href: string;
  replace?: boolean;
};

/**
 * An in-app link.
 *
 * Keeps `href` rather than react-router's `to` — not to imitate Next, but
 * because `href` is the attribute the thing actually renders, every author here
 * already writes it, and renaming it across forty-two files would have been a
 * diff with no reader.
 *
 * An absolute URL, a mailto:, or an anchor is handed to a plain `<a>`: the
 * router would otherwise try to match `https://…` as a path and land on the
 * not-found route. `target="_blank"` gets `rel="noopener"` on the way past.
 *
 * `forwardRef` is load-bearing — several call sites are `<Button asChild>`,
 * and Radix's Slot clones the child and needs a ref to forward to.
 */
export const Link = forwardRef<HTMLAnchorElement, Props>(function Link(
  { href, replace, target, rel, ...props },
  ref,
) {
  const external = /^([a-z][a-z0-9+.-]*:|\/\/)/i.test(href) || href.startsWith('#');

  if (external) {
    return (
      // The content is whatever the caller nested, arriving through {...props};
      // the rule cannot see through a wrapper component to find it.
      // eslint-disable-next-line jsx-a11y/anchor-has-content
      <a
        {...props}
        ref={ref}
        href={href}
        target={target}
        rel={target === '_blank' ? (rel ?? 'noopener noreferrer') : rel}
      />
    );
  }

  return <RouterLink {...props} ref={ref} to={href} replace={replace} target={target} rel={rel} />;
});

export default Link;
